import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, FormControl, InputLabel, Select, MenuItem, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SearchBar from '../../components/SearchBar';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import { formatDateTime } from '../../utils/formatters';
import { getInquiries } from '../../api/endpoints/astrologers';

const statusColor = { new: 'info', contacted: 'warning', closed: 'default' };

const AstrologerInquiryList = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchInquiries = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await getInquiries(params);
      setInquiries(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, debouncedSearch]);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const columns = [
    { id: 'name', label: 'Name', sortable: true, render: (row) => (
      <Typography variant="body2" fontWeight={500}>{row.name}</Typography>
    )},
    { id: 'mobile', label: 'Mobile', render: (row) => (
      <a href={`tel:${row.mobile}`} style={{ textDecoration: 'none' }}>
        <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LocalPhoneIcon fontSize="inherit" />{row.mobile}
        </Typography>
      </a>
    )},
    { id: 'email', label: 'Email', render: (row) => (
      <a href={`mailto:${row.email}`} style={{ textDecoration: 'none' }}>
        <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <EmailIcon fontSize="inherit" />{row.email}
        </Typography>
      </a>
    )},
    { id: 'status', label: 'Status', render: (row) => (
      <StatusChip status={row.status} color={statusColor[row.status]} />
    )},
    { id: 'createdAt', label: 'Submitted', render: (row) => formatDateTime(row.createdAt) },
    { id: 'actions', label: '', render: (row) => (
      <Tooltip title="View"><IconButton size="small" onClick={() => navigate(`/astrologer-inquiries/${row._id}`)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
    )},
  ];

  return (
    <Box>
      <PageHeader title="Astrologer Inquiries" subtitle="Registration requests from aspiring astrologers" />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <SearchBar value={search} onChange={setSearch} placeholder="Search by name, email or mobile..." sx={{ minWidth: 300 }} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            {Object.keys(statusColor).map((s) => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <DataTable columns={columns} rows={inquiries} page={page} rowsPerPage={rowsPerPage} total={total}
          onPageChange={setPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          emptyMessage="No inquiries yet" />
      )}
    </Box>
  );
};

export default AstrologerInquiryList;
